import { motion } from "framer-motion";
import {
  ExternalLink,
  Code,
  Cloud,
  Database,
  Shield,
  Wrench,
  BarChart3,
} from "lucide-react";
import data from "../data/software.json";

const SoftwareSkills = () => {
  const dataSkills = data["Skills"];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const Skill = ({ url, skill }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 border border-secondary-200 hover:border-primary-300 group">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors duration-200">
              <img
                className="w-8 h-8 object-contain"
                src={`${skill.replace(".", "").replace(" ", "").replace("#", "sharp").toLowerCase()}.png`}
                alt={`${skill} logo`}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
              <Code className="w-8 h-8 text-secondary-600 hidden" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-secondary-900 group-hover:text-primary-600 transition-colors duration-200">
                {skill}
              </h4>
            </div>
            <ExternalLink className="w-4 h-4 text-secondary-400 group-hover:text-primary-600 transition-colors duration-200" />
          </div>
        </div>
      </a>
    </motion.div>
  );

  const SkillCard = ({ skillSetName, skillSet, icon: Icon, color }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-secondary-200"
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className={`p-3 rounded-lg ${color} bg-opacity-10`}>
          <Icon
            className={`w-6 h-6 ${color.replace("text-", "text-").replace("-600", "-600")}`}
          />
        </div>
        <h3 className="font-display font-semibold text-xl text-secondary-900">
          {skillSetName}
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {skillSet.map((skillData, index) => (
          <Skill key={index} {...skillData} />
        ))}
      </div>
    </motion.div>
  );

  const skillCategories = [
    {
      name: "Languages",
      icon: Code,
      color: "text-primary-600",
      skills: dataSkills["Languages"],
    },
    {
      name: "Frontend",
      icon: Code,
      color: "text-accent-600",
      skills: dataSkills["Frontend"],
    },
    {
      name: "Backend",
      icon: Database,
      color: "text-accent-600",
      skills: dataSkills["Backend"],
    },
    {
      name: "Cloud & DevOps",
      icon: Cloud,
      color: "text-primary-600",
      skills: dataSkills["Cloud & DevOps"],
    },
    {
      name: "CMS & Analytics",
      icon: BarChart3,
      color: "text-accent-600",
      skills: dataSkills["CMS & Analytics"],
    },
    {
      name: "Security & Reliability",
      icon: Shield,
      color: "text-secondary-600",
      skills: dataSkills["Security & Reliability"],
    },
    {
      name: "Developer Tools",
      icon: Wrench,
      color: "text-secondary-600",
      skills: dataSkills["Developer Tools"],
    },
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="font-display font-bold text-4xl md:text-5xl text-secondary-900 mb-4">
            Technical Skills
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            A comprehensive overview of my technical expertise and the
            technologies I use to build secure, scalable, and high-performance
            applications.
          </p>
        </motion.div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {skillCategories.map((category, index) => (
            <SkillCard
              key={index}
              skillSetName={category.name}
              skillSet={category.skills}
              icon={category.icon}
              color={category.color}
            />
          ))}
        </div>

        {/* Summary */}
        <motion.div variants={itemVariants} className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
              Full-Stack Development Expertise
            </h3>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              With over 6 years of experience, I specialize in designing and
              deploying secure, high-scale systems across the full stack. My
              expertise spans from frontend frameworks and user experience
              design to backend APIs, infrastructure automation, and cloud
              architecture. I'm passionate about solving complex engineering
              problems in meaningful domains like health tech.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SoftwareSkills;
